import React, { useState, useEffect } from "react";
import { VideoMetadata } from "../video.model";
import { cloudVideoStorage } from "../utils/cloudVideoStorage";

interface EditModalProps {
  video: VideoMetadata;
  onSave: (id: string, name: string, description: string) => void;
  onClose: () => void;
}

const EditModal: React.FC<EditModalProps> = ({ video, onSave, onClose }) => {
  const [name, setName] = useState(video.name);
  const [description, setDescription] = useState(video.description);

  const handleSave = () => {
    onSave(video.id, name, description);
    onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "2rem",
          borderRadius: "8px",
          width: "90%",
          maxWidth: "500px",
        }}
      >
        <h2>Edit Video Details</h2>

        <div style={{ marginBottom: "1rem" }}>
          <label
            style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "bold",
            }}
          >
            Video Name:
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "16px",
            }}
          />
        </div>

        <div style={{ marginBottom: "1.5rem" }}>
          <label
            style={{
              display: "block",
              marginBottom: "0.5rem",
              fontWeight: "bold",
            }}
          >
            Description:
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            style={{
              width: "100%",
              padding: "8px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "16px",
              resize: "vertical",
            }}
          />
        </div>

        <div
          style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: "10px 20px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

const VideoLibrary: React.FC = () => {
  const [videos, setVideos] = useState<VideoMetadata[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [editingVideo, setEditingVideo] = useState<VideoMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [storageInfo, setStorageInfo] = useState<{
    used: number;
    quota: number;
  } | null>(null);

  useEffect(() => {
    loadVideos();
    loadStorageInfo();
  }, []);

  const loadStorageInfo = async () => {
    try {
      const info = await cloudVideoStorage.getStorageUsage();
      setStorageInfo(info);
    } catch (err) {
      console.error("Error loading storage info:", err);
    }
  };

  const loadVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      const savedVideos = await cloudVideoStorage.getVideos();
      setVideos(
        savedVideos.map((video: any) => ({
          ...video,
          recordedAt: new Date(video.recordedAt),
        })),
      );
    } catch (err) {
      setError("Failed to load videos");
      console.error("Error loading videos:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadVideoBlob = async (videoId: string): Promise<string | null> => {
    try {
      const blob = await cloudVideoStorage.getVideoBlob(videoId);
      if (blob) {
        return URL.createObjectURL(blob);
      }
      return null;
    } catch (err) {
      console.error("Error loading video blob:", err);
      return null;
    }
  };

  const playVideo = async (videoId: string) => {
    if (selectedVideo === videoId) {
      setSelectedVideo(null);
      setVideoUrl(null);
    } else {
      const url = await loadVideoBlob(videoId);
      if (url) {
        setSelectedVideo(videoId);
        setVideoUrl(url);
      }
    }
  };

  const deleteVideo = async (videoId: string) => {
    try {
      await cloudVideoStorage.deleteVideo(videoId);
      const updatedVideos = videos.filter((video) => video.id !== videoId);
      setVideos(updatedVideos);

      if (selectedVideo === videoId) {
        setSelectedVideo(null);
        setVideoUrl(null);
      }

      loadStorageInfo();
    } catch (err) {
      console.error("Error deleting video:", err);
      alert("Failed to delete video. Please try again.");
    }
  };

  const clearAllVideos = async () => {
    try {
      await cloudVideoStorage.clearAllVideos();
      setVideos([]);
      setSelectedVideo(null);
      setVideoUrl(null);
      loadStorageInfo();
      alert("All videos have been deleted.");
    } catch (err) {
      console.error("Error clearing videos:", err);
      alert("Failed to clear videos. Please try again.");
    }
  };

  const editVideo = (video: VideoMetadata) => {
    setEditingVideo(video);
  };

  const saveVideoEdit = async (
    id: string,
    name: string,
    description: string,
  ) => {
    try {
      await cloudVideoStorage.updateVideo(id, { name, description });
      const updatedVideos = videos.map((video) =>
        video.id === id ? { ...video, name, description } : video,
      );
      setVideos(updatedVideos);
    } catch (err) {
      console.error("Error updating video:", err);
      alert("Failed to update video. Please try again.");
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div style={{ padding: "2rem" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <h1>Video Library</h1>

        {storageInfo && storageInfo.quota > 0 && (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#e3f2fd",
              borderRadius: "5px",
              fontSize: "14px",
            }}
          >
            Storage: {formatBytes(storageInfo.used)} /{" "}
            {formatBytes(storageInfo.quota)}(
            {Math.round((storageInfo.used / storageInfo.quota) * 100)}% used)
          </div>
        )}
      </div>

      {videos.length > 0 && (
        <div style={{ marginBottom: "1rem" }}>
          <button
            onClick={clearAllVideos}
            style={{
              padding: "8px 16px",
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Clear All Videos
          </button>
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "1rem",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            border: "1px solid #f5c6cb",
            borderRadius: "4px",
            marginBottom: "1rem",
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
          }}
        >
          <h3>Loading videos...</h3>
        </div>
      ) : videos.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "3rem",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
          }}
        >
          <h3>No videos yet</h3>
          <p>Start a livestream and record it to see your videos here!</p>
        </div>
      ) : (
        <>
          {videoUrl && (
            <div style={{ marginBottom: "2rem" }}>
              <h3>Now Playing</h3>
              <video
                src={videoUrl}
                controls
                style={{
                  width: "100%",
                  maxWidth: "800px",
                  height: "auto",
                  backgroundColor: "#000",
                  borderRadius: "8px",
                }}
              />
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: "1rem",
            }}
          >
            {videos.map((video) => (
              <div
                key={video.id}
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  padding: "1rem",
                  backgroundColor:
                    selectedVideo === video.id ? "#e3f2fd" : "white",
                }}
              >
                <div
                  style={{
                    width: "100%",
                    height: "150px",
                    backgroundColor: "#333",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    marginBottom: "1rem",
                    cursor: "pointer",
                  }}
                  onClick={() => playVideo(video.id)}
                >
                  {selectedVideo === video.id ? "⏸️" : "▶️"}
                </div>

                <h4 style={{ margin: "0 0 0.5rem 0" }}>{video.name}</h4>
                <p
                  style={{
                    margin: "0 0 0.5rem 0",
                    color: "#666",
                    fontSize: "14px",
                    lineHeight: "1.4",
                  }}
                >
                  {video.description}
                </p>

                <div
                  style={{
                    fontSize: "12px",
                    color: "#888",
                    marginBottom: "1rem",
                  }}
                >
                  <div>Duration: {formatDuration(video.duration)}</div>
                  <div>Recorded: {formatDate(video.recordedAt)}</div>
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => playVideo(video.id)}
                    style={{
                      padding: "6px 12px",
                      backgroundColor: "#007bff",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    {selectedVideo === video.id ? "Stop" : "Play"}
                  </button>
                  <button
                    onClick={() => editVideo(video)}
                    style={{
                      padding: "6px 12px",
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteVideo(video.id)}
                    style={{
                      padding: "6px 12px",
                      backgroundColor: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {editingVideo && (
        <EditModal
          video={editingVideo}
          onSave={saveVideoEdit}
          onClose={() => setEditingVideo(null)}
        />
      )}
    </div>
  );
};

export default VideoLibrary;
